// packages/shared-hooks/src/clients/useRestoreClientVersion.ts
//
// `POST /clients/:id/versions/:versionId/restore` — applies a historical
// snapshot to the live aggregate. Returns `{ client, version }` (both
// refreshed): the new aggregate state and the freshly-emitted
// `CLIENT_VERSION_RESTORE` history row. Backend rejects with 409
// DOMAIN_CONFLICT when applying the snapshot would collide with a
// uniqueness slot now held by a different aggregate.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RestoreClientVersionResponseSchema,
  type RestoreClientVersionResponse,
} from '@magimp/shared';
import { useApiClient } from '../api/context.js';
import { clientsKeys } from './keys.js';

export function useRestoreClientVersion(clientId: string, versionId: string) {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<RestoreClientVersionResponse, unknown, void>({
    mutationFn: () =>
      client.post<RestoreClientVersionResponse>(
        `/clients/${clientId}/versions/${versionId}/restore`,
        undefined,
        {},
        RestoreClientVersionResponseSchema,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clientsKeys.detail(clientId) });
      void qc.invalidateQueries({ queryKey: clientsKeys.versions(clientId) });
      void qc.invalidateQueries({ queryKey: clientsKeys.all });
    },
  });
}
